import HomeScreen from './screens/HomeScreen'
import './App.css'

//fonts
import './fonts/JeremyMoore.otf'
import './fonts/JeremyMoore.ttf'
import './fonts/JeremyMoore.woff'
import './fonts/JeremyMoore.woff2'
import './fonts/InterTight.ttf'

import { BrowserRouter, Route, Routes } from 'react-router-dom'
import ProfileScreen from './screens/ProfileScreen'
import { useDispatch, useSelector } from 'react-redux'
import { getUser } from './app/features/userSlice'
import EpisodeScreen from './screens/EpisodeScreen'
import RecoverPasswordScreen from './screens/RecoverPasswordScreen'
import EmailScreen from './screens/EmailScreen'
import SignInScreen from './screens/SignInScreen'
import SignUpScreen from './screens/SignUpScreen'
import PlanScreen from './screens/PlanScreen'
import PixCheckoutScreen from './screens/PixCheckoutScreen'
import SubscriptionScreen from './screens/SubscriptionScreen'
import UnpaidScreen from './screens/UnpaidScreen'
import AwaitingPaymentScreen from './screens/AwaitingPaymentScreen'
import SerieScreen from './screens/SerieScreen'
import DraftScreen from './screens/DraftScreen'
import SearchScreen from './screens/SearchScreen'
import { useEffect, useRef } from 'react'
import OneSignal from 'react-onesignal'
import CollectTaxIDScreen from './screens/CollectTaxIDScreen'
import ScheduleScreen from './screens/ScheduleScreen'
import MobileRedirect from './components/MobileRedirect'
import BlockedScreen from './screens/BlockedScreen'
import PendingPaymentBanner from './components/PendingPaymentBanner'
import PendingPaymentModal from './components/PendingPaymentModal'
import { clearSubscriptionStatus, fetchSubscriptionStatus } from './app/features/subscriptionStatusSlice'

const SUBSCRIPTION_STATUS_POLL_MS = 5 * 60 * 1000 // 5 min

function App() {
  const user = useSelector(getUser)
  const dispatch = useDispatch()

  const oneSignalReady = useRef(null)
  const oneSignalExternalId = useRef(null)

  useEffect(() => {
    oneSignalReady.current = OneSignal.init({
      appId: process.env.REACT_APP_ONESIGNAL_APP_ID,
      notifyButton: {
        enable: false,
      },
      notificationClickHandlerAction: "navigate",
      autoRegister: true,
      autoResubscribe: true,
    }).catch(() => {});
  }, []);

  // Individual pushes target the OneSignal external id = the member's email
  // (same as the app), so this browser is tied to the logged member and
  // released on logout — whichever screen dispatched it.
  const userEmail = user?.email
  useEffect(() => {
    const ready = oneSignalReady.current ?? Promise.resolve()
    if (userEmail) {
      if (oneSignalExternalId.current === userEmail) return
      oneSignalExternalId.current = userEmail
      ready.then(() => OneSignal.login(userEmail)).catch(() => {})
      return
    }
    if (oneSignalExternalId.current) {
      oneSignalExternalId.current = null
      ready.then(() => OneSignal.logout()).catch(() => {})
    }
  }, [userEmail]);

  // Subscription status fetch + light polling. Only runs while logged in.
  useEffect(() => {
    if (!user) {
      // Never let the next member on this browser see the previous one's
      // pending payment (its invoice URL is a bearer link).
      dispatch(clearSubscriptionStatus())
      return
    }
    dispatch(fetchSubscriptionStatus())
    const t = setInterval(
      () => dispatch(fetchSubscriptionStatus()),
      SUBSCRIPTION_STATUS_POLL_MS,
    )
    return () => clearInterval(t)
  }, [user, dispatch])

  return (
    <div className="app">
      <BrowserRouter>
        <MobileRedirect />
        {user && <PendingPaymentBanner />}
        {user && <PendingPaymentModal />}
        <Routes>
          <Route path="/" element={!user ? <EmailScreen /> : <HomeScreen />} />
          <Route path="/signin" element={<SignInScreen />} />
          <Route path="/signup" element={<SignUpScreen />} />
          <Route path="/plan" element={<PlanScreen />} />
          <Route path="/plan/pix" element={!user ? <EmailScreen /> : <PixCheckoutScreen />} />
          <Route path="/subscription" element={!user ? <EmailScreen /> : <SubscriptionScreen />} />
          <Route path="/unpaid" element={!user ? <EmailScreen /> : <UnpaidScreen />} />
          <Route path="/awaiting-payment" element={!user ? <EmailScreen /> : <AwaitingPaymentScreen />} />
          <Route path="/collect-tax-id" element={!user ? <EmailScreen /> : <CollectTaxIDScreen />} />
          <Route path="/profile" element={!user ? <EmailScreen /> : <ProfileScreen />} />
          <Route path="/episodes/:id" element={!user ? <EmailScreen /> : <EpisodeScreen />} />
          <Route path="/series/:id" element={!user ? <EmailScreen /> : <SerieScreen />} />
          <Route path="/draft" element={<DraftScreen />} />
          <Route path="/recover-password" element={<RecoverPasswordScreen />} />
          <Route path="/recover-password/:token" element={<RecoverPasswordScreen />} />
          <Route path="/search/:title" element={!user ? <EmailScreen /> : <SearchScreen />} />
          <Route path="/schedule" element={!user ? <EmailScreen /> : <ScheduleScreen />} />
          <Route path="/blocked" element={<BlockedScreen />} />
        </Routes>
      </BrowserRouter>
    </div>
  )
}

export default App
